import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import type { ComponentProps, ReactNode } from "react";

/**
 * The app's one icon-button primitive — a thin wrapper over MUI IconButton for
 * icon-only actions (grid edit/copy, filter search/reset, …). Complements
 * {@link Button} (the text/action primitive); together they are the only two
 * button components, so restyling icon actions is a one-file change.
 *
 * - `label` is the required accessible name (icon buttons have no visible text).
 * - `loading` swaps the icon for a spinner and disables the button.
 * - `size` defaults to `small` (the grid/toolbar norm); pass through for others.
 */
export interface AppIconButtonProps extends Omit<ComponentProps<typeof IconButton>, "aria-label"> {
  /** Accessible name for the icon action. */
  label: string;
  /** The icon element. */
  children: ReactNode;
  /** Swap the icon for a spinner + disable while the action is in flight. */
  loading?: boolean;
}

export function AppIconButton({
  label,
  loading = false,
  disabled = false,
  size = "small",
  children,
  ...rest
}: AppIconButtonProps) {
  return (
    <IconButton aria-label={label} size={size} disabled={disabled || loading} {...rest}>
      {loading ? <CircularProgress size={18} color="inherit" /> : children}
    </IconButton>
  );
}

export default AppIconButton;
